
import purl from '@allmarkedup/purl/purl';
import bowser from 'bowser';

import { getTranslator } from '../js/i18n';
import { usePluginDiagnostic } from './plugin_diagnostic';

const t = getTranslator();

export function usePluginApi(_options) {
    const clientId = Math.floor(Math.random() * 10000);
    let generation = clientId;
    const outbound = new Map();

    const {
        defaultTimeout,
    } = {
        //DSPro may sometimes be slow to answer (CRL download, TSL update, etc..)
        defaultTimeout: 300000,
        ..._options,
    };

    function handleResponse(fn, message) {
        const id = Number(message.generation);
        const response = outbound.get(id);
        if(!response)
            return undefined;

        if(fn == 'err_msg') {
            const err = new Error(message.err_msg);
            err.err_code = message.err_code;
            outbound.delete(id);
            return response.process(err);
        }
        //check function
        if(response.function_name != fn)
            return undefined;

        outbound.delete(id);
        return response.process(null, message);
    }

    function handleMessage(evt) {
        const data = evt.data;
        if(!data)
            return undefined;

        if(data.err_msg && (typeof (data.generation) === 'number')) {
            //special error handling
            return handleResponse('err_msg', data);
        }

        let fn;
        let message;
        let messageFound = false;
        for(fn in data) {
            message = data[fn];
            if(message && (typeof (message.generation) === 'number')) {
                messageFound = true;
                break;
            }
        }
        if(!messageFound)
            return undefined;
        //quick check for requests
        if((fn.length <= 4) || (fn.substring(0, 4) != 'repl'))
            return undefined;
        fn = fn.substring(4);
        return handleResponse(fn, message);
    }

    function send(fn, message, options, cb) {
        if(typeof (options) == 'function') {
            cb = options;
            options = null;
        }
        if(!options) {
            options = {};
        }
        const promise = _send(fn, message, options);
        if(!cb) {
            return promise;
        }
        promise.then((data) => cb?.(null, data)).catch((err) => cb?.(err));
        return undefined;
    }

    function _send(fn, message, options) {
        return new Promise((resolve, reject) => {
            let timer = null;
            const response = function(err, data) {
                clearTimeout(timer);
                return err ? reject(err) : resolve(data);
            };

            if(!message)
                message = {};
            message.generation = generation++;
            const id = message.generation;
            const _response = {
                function_name: fn,
                process: response,
            };
            outbound.set(id, _response);
            if((typeof (options.timeout) === 'number') && (options.timeout <= 0)) {
                //timeout is disabled
            } else {
                timer = setTimeout(function() {
                    if(outbound.has(id)) {
                        outbound.delete(id);
                        response(new Error(t('common.timeout')));
                    }
                }, options.timeout || defaultTimeout);
            }

            const data = {};
            data['req' + fn] = message;
            //temp workaround for incorrect ExtensionVersion handling
            data.generation = message.generation;
            window.postMessage(data);
        });
    }

    window.addEventListener('message', handleMessage);
    let initialized = true;

    function shutdown() {
        if(initialized) {
            window.removeEventListener('message', handleMessage);
            initialized = false;
        }
    }

    return {
        send,
        shutdown,
    };
}

export function cmpVersions(a, b) {
    let i;
    let diff;
    const regExStrip0 = /((?:\.|,)0+)+$/;
    const regExSplit = /(?:\.|,)/;
    const segmentsA = a.replace(regExStrip0, '').split(regExSplit);
    const segmentsB = b.replace(regExStrip0, '').split(regExSplit);
    const l = Math.min(segmentsA.length, segmentsB.length);

    for(i = 0; i < l; i++) {
        diff = parseInt(segmentsA[i], 10) - parseInt(segmentsB[i], 10);
        if(diff) {
            return diff;
        }
    }
    return segmentsA.length - segmentsB.length;
}

export function notInstalledError(what, err) {
    err.no_show_error = true;
    err.not_installed = what;
    return err;
}

export const PLUGIN_MODE = {
    STANDALONE: 1,
    DSPRO: 2,
};

export function useNephalemApi(_options) {
    const options = {
        //extension should answer quickly
        extensionWaitTimeout: 1000,
        //give native part more time to load
        pluginWaitTimeout: 5000,
        ..._options,
    };
    const {
        extensionWaitTimeout,
        pluginWaitTimeout,
        extensionVersion,
        extensionVersionRecommended,
        pluginVersion,
        pluginVersionRecommended,
        pluginDiagnostic,
        guiHelper,
    } = options;

    const { send, shutdown } = usePluginApi(options);

    function getBaseUrl() {
        //we get APP_BASE from wizard page, but show link on main page
        //because of this all relative paths are broken
        //we need to make path absolute
        const url = purl();
        return url.attr('base') + url.attr('directory') + APP_BASE;
    }

    function getExtensionUrl() {
        if(bowser.firefox) {
            return `${getBaseUrl()}cloud-signer-package.jsp`;
        }
        return `${getBaseUrl()}cloud-signer-chrome-extension.jsp`;
    }

    function getPluginUrl() {
        return `${getBaseUrl()}cloud-signer-package.jsp`;
    }

    function markExtensionErrorDetails(err) {
        const messages = [];
        messages.push(err
            ? t('plugin.extension_not_installed')
            : t('plugin.extension_update_required', { version: extensionVersion }));
        let message = '';
        if(bowser.firefox) {
            message = t('plugin.extension_firefox_install', { url: getExtensionUrl() });
        } else {
            message = t('plugin.extension_install', { url: getExtensionUrl() });
        }
        messages.push(message);
        messages.push(t('plugin.restart_browser'));

        message = '<p>' + messages.join('</p><p>') + '</p>';
        return pluginDiagnostic.markFailed('info_extension', err, message);
    }

    function markPluginErrorDetails(err) {
        const messages = [];
        messages.push(err
            ? t('plugin.connector_not_installed')
            : t('plugin.connector_update_required', { version: pluginVersion }));
        let message = '';
        message = t('plugin.connector_install', { url: getPluginUrl() });
        messages.push(message);
        messages.push(t('plugin.restart_browser'));

        message = '<p>' + messages.join('</p><p>') + '</p>';
        return pluginDiagnostic.markFailed('info_plugin', err, message);
    }

    function init() {
        let extensionVersionCurrent;
        let pluginVersionCurrent;
        return Promise.resolve()
            .then(() => {
                pluginDiagnostic.setCurrentVersion('info_browser', `${bowser.name} ${bowser.version} (${bowser.osname})`);
                //first check JavaScript extension part, we don't check browser first, because browser detection may break sometime
                //and extension will still work
                if(!extensionVersion)
                    return undefined;
                const promise = send('ExtensionVersion', {}, { timeout: extensionWaitTimeout });
                return promise
                    .then((data) => {
                        extensionVersionCurrent = data.version;
                        pluginDiagnostic.setCurrentVersion('info_extension', extensionVersionCurrent, extensionVersionRecommended);
                        if(cmpVersions(extensionVersionCurrent, extensionVersion) < 0) {
                            return Promise.reject(markExtensionErrorDetails());
                        }
                        return Promise.resolve();
                    })
                    .catch((err) => {
                        if(err.pluginDiagnostic || err.softUpdateAborted)
                            return Promise.reject(err);
                        if(!(bowser.chrome || bowser.chromium || bowser.firefox) || !bowser.windows) {
                            const err0 = pluginDiagnostic.markFailed(
                                'info_browser',
                                err,
                                t('plugin.browser_required'),
                            );
                            return Promise.reject(err0);
                        }
                        return Promise.reject(markExtensionErrorDetails(err));
                    });
            })
            .then(() => {
                //check native plugin part
                if(!pluginVersion)
                    return undefined;
                const promise = send('NativeVersion', {}, { timeout: pluginWaitTimeout });
                return promise
                    .then((data) => {
                        pluginVersionCurrent = data.version;
                        pluginDiagnostic.setCurrentVersion('info_plugin', pluginVersionCurrent, pluginVersionRecommended);
                        if(cmpVersions(pluginVersionCurrent, pluginVersion) < 0) {
                            return Promise.reject(markPluginErrorDetails());
                        }
                        return Promise.resolve();
                    })
                    .catch((err) => {
                        if(err.pluginDiagnostic)
                            return Promise.reject(err);
                        return Promise.reject(markPluginErrorDetails(err));
                    });
            })
            .then(() => {
                const moduleRows = [];
                if(guiHelper
                    && extensionVersionCurrent
                    && extensionVersionRecommended
                    && (cmpVersions(extensionVersionCurrent, extensionVersionRecommended) < 0)
                ) {
                    moduleRows.push({
                        info: 'info_extension',
                        downloadUrl: getExtensionUrl(),
                    });
                }
                if(guiHelper
                    && pluginVersionCurrent
                    && pluginVersionRecommended
                    && (cmpVersions(pluginVersionCurrent, pluginVersionRecommended) < 0)
                ) {
                    moduleRows.push({
                        info: 'info_plugin',
                        downloadUrl: getPluginUrl(),
                    });
                }
                if(moduleRows.length) {
                    return Promise.resolve()
                        .then(() => pluginDiagnostic.showSoftUpdateDialog(guiHelper, moduleRows))
                        .then((shouldContinue) => {
                            if(!shouldContinue) {
                                const err = new Error(t('plugin.aborted'));
                                err.no_show_error = true;
                                err.softUpdateAborted = true;
                                return Promise.reject(err);
                            }
                            return Promise.resolve();
                        });
                }
                return Promise.resolve();
            })
            .catch((err) => {
                shutdown();
                return Promise.reject(err);
            });
    }

    return {
        init,
        shutdown,
        send,
        getExtensionUrl,
        getPluginUrl,
    };
}

export function useNephalemDsproApi(_guiHelper, _api, _options) {
    const options = {
        extensionVersion: '1.0.0.7',
        pluginVersion: '1.0.0.7',
        dsproVersion: '5,0,3,426',
        pluginDiagnostic: usePluginDiagnostic({
            title: t('plugin.diagnostic_pkcs11'),
        }),
        pluginMode: PLUGIN_MODE.DSPRO,
        ..._options,
    };
    const {
        dsproVersion,
        pluginDiagnostic,
    } = options;
    const { init: _init, send, shutdown } = useNephalemApi(options);

    function markDSProErrorDetails(err) {
        const messages = [];
        messages.push(err
            ? t('plugin.dspro_not_installed')
            : t('plugin.dspro_update_required', { version: dsproVersion }));
        messages.push(t('plugin.dspro_install'));
        messages.push(t('plugin.restart_browser'));
        const message = '<p>' + messages.join('</p><p>') + '</p>';
        return pluginDiagnostic.markFailed('info_dspro', err, message);
    }

    function init() {
        return Promise.resolve()
            .then(() => _init())
            .then(() => {
                if(!dsproVersion)
                    return undefined;
                //this will start DSPro, so longer timeout is required
                //checkDSPro
                let timeout = null;
                if(_guiHelper) {
                    //show message after 1 second for impatient users
                    timeout = setTimeout(function() {
                        if(timeout) {
                            _guiHelper.alert2(t('plugin.dspro_starting'), 'success');
                            timeout = null;
                        }
                    }, 2000);
                }
                const promise = send('Version', {});
                return promise
                    .then((data) => {
                        pluginDiagnostic.setCurrentVersion('info_dspro', data.version);
                        if(cmpVersions(data.version, dsproVersion) < 0) {
                            return Promise.reject(markDSProErrorDetails());
                        }
                        return Promise.resolve();
                    })
                    .catch((err) => {
                        if(err.pluginDiagnostic)
                            return Promise.reject(err);
                        return Promise.reject(markDSProErrorDetails(err));
                    })
                    .finally(() => {
                        clearTimeout(timeout);
                        timeout = null;
                    });
            })
            .then(() => {
                if(!_api)
                    return undefined;
                const promise = Promise.resolve()
                    .then(() => send('GetChallenge', {}))
                    .then(({ challenge }) => _api.execApi('localpkcs11signchallenge.cmd', { challenge }, { noalert: true, errors: true, waiting: false }))
                    .then(({ encryptedChallenge }) => send('VerifyChallenge', { encryptedChallenge }))
                    .catch((err) => Promise.reject(pluginDiagnostic.markFailed(undefined, err)));
                return promise;
            })
            .catch((err) => {
                shutdown();
                return Promise.reject(err);
            });
    }

    return {
        init,
        shutdown,
        send,
        pluginDiagnostic,
    };
}

export function useNephalemWacomApi({ guiHelper }) {
    const options = {
        extensionVersion: '1.0.0.14',
        extensionVersionRecommended: '1.0.0.15',
        pluginVersion: '1.1.0.12',
        pluginVersionRecommended: '1.1.0.18',
        pluginDiagnostic: usePluginDiagnostic({
            title: t('plugin.diagnostic_wacom'),
        }),
        pluginMode: PLUGIN_MODE.STANDALONE,
        guiHelper,
    };
    const {
        pluginDiagnostic,
    } = options;
    const supportedVersions = {
        'STU-430': '1.1.0.17',
        'STU-500': '1.1.0.18',
        'STU-530': '1.1.0.12',
        'STU-540': '1.1.0.12',
    };

    const api = useNephalemApi(options);

    function markTabletPluginErrorDetails(requiredPluginVersion) {
        const messages = [];
        messages.push(t('plugin.tablet_not_supported'));
        messages.push(t('plugin.minimum_version_required', { version: requiredPluginVersion }));
        messages.push(t('plugin.connector_install', { url: api.getPluginUrl() }));
        messages.push(t('plugin.restart_browser'));

        const message = '<p>' + messages.join('</p><p>') + '</p>';
        return pluginDiagnostic.markFailed('info_plugin', undefined, message);
    }

    return {
        ...api,
        pluginDiagnostic,
        supportedVersions,
        markTabletPluginErrorDetails,
    };
}
